/**
 * Pure helpers behind the drag zoom on the metrics page (TRA-1592): one
 * zoom for the whole page, so a drag on any chart zooms every chart to
 * that stretch and the page's arrows then move every chart together. A
 * zoom reads nothing: each chart cuts the data it has loaded and draws
 * it at once. The zoom lives in memory, not the URL; `?hours=` and
 * `?end=` stay what metricsPan makes them.
 *
 * The state is the replicas chart's PanState: the window end from the
 * URL (null at now) and the zoom, always inside that window. `now` is a
 * parameter everywhere so the tests pin it.
 */

import {
  HOUR_MS,
  atEnd,
  stepSpan,
  visibleSpan,
  type PanDirection,
  type PanState,
} from '@/components/models/home/replicaPan'
import { dragRange, type TimeRange } from '@/components/models/home/replicaZoom'
import { MAX_METRICS_HOURS } from '@/components/models/home/scalingWindow'
import {
  METRICS_PAN_LIMIT_MS,
  metricsBucketMs,
  onGrid,
  steppedEnd,
} from '@/components/models/metrics/metricsPan'

/** How long a zoom must hold still before the main cards read it at full detail. */
export const SHARPEN_AFTER_MS = 400

/**
 * The zoom a drag across a bucket chart means: from the first bucket
 * dragged to the end of the last one, so the stretch holds whole buckets.
 * A drag that stays in one bucket is a click, not a zoom.
 */
export function bucketDragRange(from: number, to: number, bucketMs: number): TimeRange | null {
  if (from === to) return null
  return dragRange(Math.min(from, to), Math.max(from, to) + bucketMs)
}

/** The part of `range` inside `bounds`; the range itself when it already is. */
function clamped(range: TimeRange, bounds: TimeRange): TimeRange {
  return {
    start: Math.max(range.start, bounds.start),
    end: Math.min(range.end, bounds.end),
  }
}

function inside(range: TimeRange, bounds: TimeRange): boolean {
  return range.start >= bounds.start && range.end <= bounds.end
}

/**
 * The end of the window of `hours` a zoom moved into, on the grid: moved
 * later, the zoom sits at the window's start, moved earlier, at its end,
 * so the steps after this one stay inside the loaded window and read
 * nothing. Never past now, and never starting before the 30 days the page
 * can reach.
 */
function windowAround(zoom: TimeRange, hours: number, direction: PanDirection, now: number): number | null {
  const width = hours * HOUR_MS
  if (direction === 'later') return onGrid(zoom.start + width, hours, now)
  return onGrid(Math.max(zoom.end, now - METRICS_PAN_LIMIT_MS + width), hours, now, true)
}

/**
 * The state after an arrow press. Not zoomed, the window moves as the
 * arrows always did (steppedEnd). Zoomed, the zoom moves by half its
 * width: while it stays inside the loaded window the end is unchanged and
 * nothing is read; once it leaves, the window follows it (windowAround)
 * and the main read runs for the new window. A grid edge can land a sliver
 * of the zoom outside the new window, so the zoom is cut to it. The clock
 * is the default `now`, so the hook's handler stays free of clock reads.
 */
export function steppedPage(
  state: PanState,
  hours: number,
  direction: PanDirection,
  now: number = Date.now(),
): PanState {
  if (state.zoom === null) return atEnd(steppedEnd(state.end, hours, direction, now))
  const next = stepSpan(state.zoom, direction, now, METRICS_PAN_LIMIT_MS)
  if (inside(next, visibleSpan(atEnd(state.end), hours, now))) return { end: state.end, zoom: next }
  const end = windowAround(next, hours, direction, now)
  return { end, zoom: clamped(next, visibleSpan(atEnd(end), hours, now)) }
}

/** What the main read takes for a zoom alone. */
export interface SharpParams {
  hours: number
  end: number | null
}

/**
 * Where the server starts a window of `hours` ending at `end`: exactly
 * `hours` before it, or before the boundary after now when `end` is null
 * for now (bucket_window in the api's usage_views.py).
 */
function windowStart(end: number | null, hours: number, now: number): number {
  const bucket = metricsBucketMs(hours)
  return (end ?? Math.floor(now / bucket) * bucket + bucket) - hours * HOUR_MS
}

/**
 * The main read for the zoomed stretch at full detail. It ends at the
 * first boundary at or after the zoom's end on its window's grid, so the
 * zoom's last bucket is inside it: a zoom dragged on the engine chart
 * sits on that chart's grid, not this read's, and flooring the end lost
 * that bucket (sometimes every bucket). It is as many whole hours, at
 * least one, as it takes to start at or before the zoom. More hours can
 * mean a coarser grid, so the end is snapped again until both hold. The
 * clock is the default `now`, so the page stays free of clock reads.
 */
export function sharpParams(zoom: TimeRange, now: number = Date.now()): SharpParams {
  let hours = Math.min(MAX_METRICS_HOURS, Math.max(1, Math.ceil((zoom.end - zoom.start) / HOUR_MS)))
  let end = onGrid(zoom.end, hours, now, true)
  let start = windowStart(end, hours, now)
  while (start > zoom.start && hours < MAX_METRICS_HOURS) {
    hours = Math.min(MAX_METRICS_HOURS, hours + Math.ceil((start - zoom.start) / HOUR_MS))
    end = onGrid(zoom.end, hours, now, true)
    start = windowStart(end, hours, now)
  }
  return { hours, end }
}

/** Whether two stretches are the same one, or both missing. */
export function sameRange(a: TimeRange | null, b: TimeRange | null): boolean {
  if (a === null || b === null) return a === b
  return a.start === b.start && a.end === b.end
}
